import { useCallback, useEffect, useMemo, useState } from "react";
import "./ReportsSectionCompact.css";
import { useNavigate } from "react-router-dom";
import InfoTooltip from "../components/common/InfoTooltip";
import ModalCloseX from "../components/common/ModalCloseX";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useAuth } from "../context/AuthContext";
import {
  buildSuperAdminReportHelpGuide,
  getSuperAdminHelpAnchors,
} from "../constants/superAdminReportHelpGuides";
import {
  exportSuperAdminSalesByEmployee,
  fetchSuperAdminSalesByEmployee,
  fetchSuperAdminSalesByEmployeeFilters,
} from "../api/superadmin";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";

const SCOPE_OPTIONS = [
  { value: "all", label: "All Salons" },
  { value: "business", label: "Business" },
  { value: "salon", label: "Salon" },
];

const PRESET_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "Last 7" },
  { value: "last_30_days", label: "Last 30" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "this_year", label: "This Year" },
  { value: "custom", label: "Custom" },
];

const GROUP_BY_OPTIONS = [
  { value: "employee", label: "Employee" },
  { value: "employee_day", label: "Employee + Day" },
  { value: "employee_category", label: "Employee + Category" },
  { value: "employee_item", label: "Employee + Service/Product" },
  { value: "employee_payment", label: "Employee + Payment Type" },
];

const TOOLTIPS = {
  scope:
    "Choose report reach: all salons, one business, or one salon. Presets run against each salon's local accounting date.",
  business:
    "Optional in All Salons scope to narrow to one business. Required in Business scope.",
  salon:
    "Required in Salon scope. Use with Business filter to quickly find the right salon.",
  preset:
    "Preset ranges use each salon's local accounting date (not browser time or UTC).",
  group_by:
    "Choose how rows are grouped. Employee + Payment shows payment mix split by employee.",
  include_refunds:
    "Include refund tickets and refund amounts in totals and table rows.",
  include_voided:
    "Include voided/void tickets in addition to closed/refunded statuses.",
};

const HELP_GUIDE = buildSuperAdminReportHelpGuide({
  reportKey: "sales-by-employee",
  title: "Sales by Employee (Super Admin)",
  subtitle:
    "Review employee sales across scope with groupings, refunds, and payment mix.",
  summaryLabel: "Summary KPIs",
  detailsLabel: "Employee Detail Table",
  hasExport: true,
});

const HELP_ANCHORS = getSuperAdminHelpAnchors("sales-by-employee");

const DEFAULT_FILTERS = {
  scope: "all",
  business_id: "",
  salon_id: "",
  preset: "today",
  start_date: "",
  end_date: "",
  group_by: "employee",
  include_refunds: true,
  include_voided: false,
};

const SORT_TYPE_BY_FIELD = {
  employee_name: "text",
  day: "date",
  category_name: "text",
  item_type: "text",
  item_name: "text",
  payment_type: "text",
  tickets: "number",
  net_sales: "number",
  tips: "number",
  tax: "number",
  gross: "number",
  refunds: "number",
  cash: "number",
  card: "number",
  gift_card: "number",
  other: "number",
};

const SORT_KEY_BY_COLUMN = {
  business_name: "business_name",
  salon_name: "salon_name",
  employee: "employee_name",
  date: "day",
  category: "category_name",
  item_type: "item_type",
  item_name: "item_name",
  payment_type: "payment_type",
  ticket_count: "tickets",
  net_sales: "net_sales",
  tips: "tips",
  tax: "tax",
  gross: "gross",
  refunds: "refunds",
  cash: "cash",
  card: "card",
  gift_card: "gift_card",
  other: "other",
};

function formatMoney(value) {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);
}

function formatCount(value) {
  const count = Number(value || 0);
  return (Number.isFinite(count) ? count : 0).toLocaleString("en-CA");
}

function downloadBlobCsv(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.setAttribute("download", filename);
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
}

export default function SuperAdminSalesByEmployee() {
  const navigate = useNavigate();
  const { is_super_admin } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [businesses, setBusinesses] = useState([]);
  const [salons, setSalons] = useState([]);
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [meta, setMeta] = useState({ page: 1, page_size: 50, total_rows: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState("net_sales");
  const [sortDir, setSortDir] = useState("desc");
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState("");

  const showEntityColumns = filters.scope === "all" || filters.scope === "business";

  const filteredSalons = useMemo(() => {
    if (!filters.business_id) return salons;
    return salons.filter(
      (salon) => String(salon.business_id || "") === String(filters.business_id)
    );
  }, [salons, filters.business_id]);

  const columns = useMemo(() => {
    const cols = [];
    if (showEntityColumns) {
      cols.push({ key: "business_name", label: "Business" });
      cols.push({ key: "salon_name", label: "Salon" });
    }
    cols.push({ key: "employee", label: "Employee" });
    if (filters.group_by === "employee_day") cols.push({ key: "date", label: "Date" });
    if (filters.group_by === "employee_category") {
      cols.push({ key: "category", label: "Category" });
    }
    if (filters.group_by === "employee_item") {
      cols.push({ key: "item_type", label: "Item Type" });
      cols.push({ key: "item_name", label: "Item Name" });
    }
    if (filters.group_by === "employee_payment") {
      cols.push({ key: "payment_type", label: "Payment Type" });
    }
    cols.push(
      { key: "ticket_count", label: "Tickets" },
      { key: "net_sales", label: "Net Sales" },
      { key: "tips", label: "Tips" },
      { key: "tax", label: "Tax" },
      { key: "gross", label: "Gross" },
      { key: "refunds", label: "Refunds" },
      { key: "cash", label: "Cash" },
      { key: "card", label: "Card" },
      { key: "gift_card", label: "Gift Card" },
      { key: "other", label: "Other" }
    );
    return cols;
  }, [filters.group_by, showEntityColumns]);

  const totalPages = Math.max(
    1,
    Math.ceil((meta?.total_rows || 0) / (meta?.page_size || pageSize))
  );

  useEffect(() => {
    if (!is_super_admin) return;
    let isMounted = true;
    setLoadingFilters(true);
    fetchSuperAdminSalesByEmployeeFilters()
      .then((data) => {
        if (!isMounted) return;
        setBusinesses(Array.isArray(data?.businesses) ? data.businesses : []);
        setSalons(Array.isArray(data?.salons) ? data.salons : []);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err?.message || "Unable to load report filters.");
      })
      .finally(() => {
        if (isMounted) setLoadingFilters(false);
      });
    return () => {
      isMounted = false;
    };
  }, [is_super_admin]);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "scope") {
        if (value === "all") {
          next.salon_id = "";
        }
        if (value === "business") {
          next.salon_id = "";
        }
      }
      if (key === "business_id" && next.salon_id) {
        const selectedSalon = salons.find(
          (salon) => String(salon.id) === String(next.salon_id)
        );
        if (selectedSalon && String(selectedSalon.business_id) !== String(value || "")) {
          next.salon_id = "";
        }
      }
      if (key === "salon_id" && value) {
        const selectedSalon = salons.find((salon) => String(salon.id) === String(value));
        if (selectedSalon) {
          next.business_id = String(selectedSalon.business_id);
        }
      }
      if (key === "preset" && value !== "custom") {
        next.start_date = "";
        next.end_date = "";
      }
      return next;
    });
  }, [salons]);

  const buildPayload = useCallback(
    (overrides = {}) => ({
      scope: filters.scope,
      business_id: filters.business_id || undefined,
      salon_id: filters.salon_id || undefined,
      preset: filters.preset === "custom" ? null : filters.preset,
      start_date: filters.preset === "custom" ? filters.start_date || undefined : undefined,
      end_date: filters.preset === "custom" ? filters.end_date || undefined : undefined,
      group_by: filters.group_by,
      include_refunds: Boolean(filters.include_refunds),
      include_voided: Boolean(filters.include_voided),
      sort_by: sortBy,
      sort_dir: sortDir,
      ...overrides,
    }),
    [filters, sortBy, sortDir]
  );

  const fetchReport = useCallback(
    async (
      pageOverride = null,
      pageSizeOverride = null,
      sortByOverride = null,
      sortDirOverride = null
    ) => {
      if (filters.scope === "business" && !filters.business_id) {
        setError("Choose a business for Business scope.");
        return;
      }
      if (filters.scope === "salon" && !filters.salon_id) {
        setError("Choose a salon for Salon scope.");
        return;
      }
      if (
        filters.preset === "custom" &&
        (!filters.start_date || !filters.end_date)
      ) {
        setError("Select both start and end dates for Custom preset.");
        return;
      }

      const nextPage = pageOverride || page;
      const nextPageSize = pageSizeOverride || pageSize;
      const nextSortBy = sortByOverride || sortBy;
      const nextSortDir = sortDirOverride || sortDir;
      setLoadingReport(true);
      setError("");
      try {
        const response = await fetchSuperAdminSalesByEmployee(
          buildPayload({
            page: nextPage,
            page_size: nextPageSize,
            sort_by: nextSortBy,
            sort_dir: nextSortDir,
          })
        );
        setRows(Array.isArray(response?.rows) ? response.rows : []);
        setKpis(response?.kpis || null);
        setMeta(
          response?.meta || {
            page: nextPage,
            page_size: nextPageSize,
            total_rows: 0,
            sort_by: nextSortBy,
            sort_dir: nextSortDir,
          }
        );
        setPage(nextPage);
      } catch (err) {
        setRows([]);
        setKpis(null);
        setMeta({
          page: nextPage,
          page_size: nextPageSize,
          total_rows: 0,
          sort_by: nextSortBy,
          sort_dir: nextSortDir,
        });
        setError(err?.message || "Unable to run Sales by Employee report.");
      } finally {
        setLoadingReport(false);
      }
    },
    [buildPayload, filters, page, pageSize, sortBy, sortDir]
  );

  const onRunReport = useCallback(() => {
    setPage(1);
    fetchReport(1);
  }, [fetchReport]);

  const onExportCsv = useCallback(async () => {
    try {
      const blob = await exportSuperAdminSalesByEmployee(buildPayload());
      const dateStamp = new Date().toISOString().slice(0, 10);
      downloadBlobCsv(blob, `superadmin-sales-by-employee-${dateStamp}.csv`);
    } catch (err) {
      setError(err?.message || "Unable to export CSV.");
    }
  }, [buildPayload]);

  const handleSort = useCallback(
    (columnKey) => {
      const nextSortBy = SORT_KEY_BY_COLUMN[columnKey];
      if (!nextSortBy) return;
      const type = SORT_TYPE_BY_FIELD[nextSortBy] || "text";
      const nextSortDir =
        nextSortBy === sortBy
          ? sortDir === "asc"
            ? "desc"
            : "asc"
          : type === "number"
            ? "desc"
            : "asc";
      if (nextSortBy === sortBy) {
        setSortDir(nextSortDir);
      } else {
        setSortBy(nextSortBy);
        setSortDir(nextSortDir);
      }
      setPage(1);
      fetchReport(1, null, nextSortBy, nextSortDir);
    },
    [sortBy, sortDir, fetchReport]
  );

  useEffect(() => {
    if (loadingFilters) return;
    if (!is_super_admin) return;
    fetchReport(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_super_admin, loadingFilters]);

  const renderFieldLabel = (label, key) => (
    <span className="inline-flex items-center gap-2">
      <span className={LABEL_TEXT}>{label}</span>
      {TOOLTIPS[key] ? <InfoTooltip text={TOOLTIPS[key]} label={label} /> : null}
    </span>
  );

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate("/superadmin/reports");
  };

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Sales by Employee (Super Admin)
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>

          </div>
          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div
              id={HELP_ANCHORS.overview}
              className={`rounded-2xl ${getSectionClassName(HELP_ANCHORS.overview)}`}
            >
              <ReportHelpCard
                title={HELP_GUIDE.title}
                subtitle={HELP_GUIDE.subtitle}
                sections={HELP_GUIDE.sections}
                troubleshooting={HELP_GUIDE.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            ) : null}

            <div
              id={HELP_ANCHORS.filters}
              className={`rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4 overflow-visible ${getSectionClassName(
                HELP_ANCHORS.filters
              )}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  {renderFieldLabel("Scope", "scope")}
                  <select
                    value={filters.scope}
                    onChange={(event) => setFilter("scope", event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={loadingFilters}
                  >
                    {SCOPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Business", "business")}
                  <select
                    value={filters.business_id}
                    onChange={(event) => setFilter("business_id", event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={loadingFilters}
                  >
                    <option value="">All Businesses</option>
                    {businesses.map((business) => (
                      <option key={business.id} value={business.id}>
                        {business.code} - {business.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Salon", "salon")}
                  <select
                    value={filters.salon_id}
                    onChange={(event) => setFilter("salon_id", event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={filters.scope !== "salon" || loadingFilters}
                  >
                    <option value="">Select Salon</option>
                    {filteredSalons.map((salon) => (
                      <option key={salon.id} value={salon.id}>
                        {salon.name} (ID {salon.id})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Date Preset", "preset")}
                  <select
                    value={filters.preset}
                    onChange={(event) => setFilter("preset", event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    {PRESET_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  <span className={LABEL_TEXT}>Start Date</span>
                  <input
                    type="date"
                    value={filters.start_date}
                    onChange={(event) => setFilter("start_date", event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={filters.preset !== "custom"}
                  />
                </div>
                <div>
                  <span className={LABEL_TEXT}>End Date</span>
                  <input
                    type="date"
                    value={filters.end_date}
                    onChange={(event) => setFilter("end_date", event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={filters.preset !== "custom"}
                  />
                </div>
                <div>
                  {renderFieldLabel("Group By", "group_by")}
                  <select
                    value={filters.group_by}
                    onChange={(event) => setFilter("group_by", event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    {GROUP_BY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div
                  id={HELP_ANCHORS.export}
                  className={`flex items-end gap-3 rounded-2xl ${getSectionClassName(
                    HELP_ANCHORS.export
                  )}`}
                >
                  <button
                    type="button"
                    className={`${BTN_PRIMARY} min-w-[130px]`}
                    onClick={onRunReport}
                    disabled={loadingReport || loadingFilters}
                  >
                    {loadingReport ? "Loading..." : "Run Report"}
                  </button>
                  <button
                    type="button"
                    className={`${BTN_OUTLINE} min-w-[130px]`}
                    onClick={onExportCsv}
                    disabled={loadingReport}
                  >
                    Export CSV
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-5">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={filters.include_refunds}
                    onChange={(event) => setFilter("include_refunds", event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  Include refunds
                  <InfoTooltip
                    text={TOOLTIPS.include_refunds}
                    label="Include refunds"
                  />
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={filters.include_voided}
                    onChange={(event) => setFilter("include_voided", event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  Include voided
                  <InfoTooltip text={TOOLTIPS.include_voided} label="Include voided" />
                </label>
              </div>
            </div>

            <div
              id={HELP_ANCHORS.kpis}
              className={`grid grid-cols-1 md:grid-cols-3 xl:grid-cols-7 gap-4 ${getSectionClassName(
                HELP_ANCHORS.kpis
              )}`}
            >
              {[
                { label: "Total Net Sales", value: formatMoney(kpis?.total_net_sales) },
                { label: "Total Tips", value: formatMoney(kpis?.total_tips) },
                { label: "Total Tax", value: formatMoney(kpis?.total_tax) },
                { label: "Total Gross", value: formatMoney(kpis?.total_gross) },
                { label: "Total Refunds", value: formatMoney(kpis?.total_refunds) },
                { label: "Avg Ticket", value: formatMoney(kpis?.avg_ticket_value) },
                { label: "Ticket Count", value: formatCount(kpis?.ticket_count) },
              ].map((tile) => (
                <div
                  key={tile.label}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    {tile.label}
                  </div>
                  <div className="mt-2 text-xl font-semibold text-slate-900">
                    {tile.value}
                  </div>
                </div>
              ))}
            </div>

            <div
              id={HELP_ANCHORS.details}
              className={`rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${getSectionClassName(
                HELP_ANCHORS.details
              )}`}
            >
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      {columns.map((column) => {
                        const sortKey = SORT_KEY_BY_COLUMN[column.key];
                        const isActiveSort = sortBy === sortKey;
                        return (
                          <th
                            key={column.key}
                            className="px-4 py-3 text-left font-semibold whitespace-nowrap"
                          >
                            <button
                              type="button"
                              onClick={() => handleSort(column.key)}
                              className="inline-flex items-center gap-1 text-left font-semibold text-slate-600 hover:text-slate-800"
                            >
                              <span>{column.label}</span>
                              {sortKey ? (
                                <span
                                  className={`text-[10px] leading-none ${
                                    isActiveSort ? "text-slate-700" : "text-slate-300"
                                  }`}
                                >
                                  {isActiveSort ? (sortDir === "asc" ? "^" : "v") : "-"}
                                </span>
                              ) : null}
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr
                        key={`${row.employee_id || "emp"}-${index}`}
                        className="border-t border-slate-100"
                      >
                        {columns.map((column) => (
                          <td
                            key={column.key}
                            className="px-4 py-3 text-slate-700 whitespace-nowrap"
                          >
                            {[
                              "net_sales",
                              "tips",
                              "tax",
                              "gross",
                              "refunds",
                              "cash",
                              "card",
                              "gift_card",
                              "other",
                            ].includes(column.key)
                              ? formatMoney(row[column.key])
                              : column.key === "ticket_count"
                                ? formatCount(row[column.key])
                                : row[column.key] ?? "--"}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {!rows.length ? (
                      <tr>
                        <td
                          className="px-4 py-6 text-center text-slate-500"
                          colSpan={columns.length}
                        >
                          {loadingReport ? "Loading..." : "No results for this range."}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 text-sm">
                <div className="text-slate-600">
                  Page {meta.page} of {totalPages} - {meta.total_rows} rows
                </div>
                <div className="flex items-center gap-3">
                  <select
                    value={pageSize}
                    onChange={(event) => {
                      const nextSize = Number(event.target.value);
                      setPageSize(nextSize);
                      setPage(1);
                      fetchReport(1, nextSize);
                    }}
                    className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
                  >
                    {[25, 50, 100, 200].map((size) => (
                      <option key={size} value={size}>
                        {size} / page
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => fetchReport(Math.max(1, page - 1))}
                    disabled={page <= 1 || loadingReport}
                    className="rounded-full border border-slate-300 px-3 py-1 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    onClick={() => fetchReport(Math.min(totalPages, page + 1))}
                    disabled={page >= totalPages || loadingReport}
                    className="rounded-full border border-slate-300 px-3 py-1 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
